"use client";

import React, { createContext, useContext, useEffect } from "react";

import { NextIntlClientProvider } from "next-intl";

import { useLocaleStore } from "@/store/locale.store";
import { Locale } from "@/types/i18n.types";

interface LocaleContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
}

const LocaleContext = createContext<LocaleContextType | null>(null);

export function LocaleProvider({
  children,
  messages,
  initialLocale
}: {
  children: React.ReactNode;
  messages: any;
  initialLocale: Locale;
}) {
  const { currentLocale, setLocale } = useLocaleStore();

  useEffect(() => {
    if (initialLocale !== currentLocale) {
      setLocale(initialLocale);
    }
  }, [initialLocale, currentLocale, setLocale]);

  return (
    <LocaleContext.Provider value={{ locale: currentLocale, setLocale }}>
      <NextIntlClientProvider
        locale={currentLocale}
        messages={messages}
        now={new Date()}
      >
        {children}
      </NextIntlClientProvider>
    </LocaleContext.Provider>
  );
}

export const useLocale = () => {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error("useLocale must be used within LocaleProvider");
  }
  return context;
};
